import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ShippingApp.css';

const VENDORS = {
  '1001': 'FastTrack Logistics Inc',
  '1002': 'BlueLine Freight Services',
  '2050': 'Global Parcel Network',
  '3214': 'Micro Shipping Private Ltd',
  '4400': 'Express Cargo Solutions',
  '5500': 'Pioneer Delivery Corp',
  '6789': 'Rapid Route Transporters',
  '7777': 'Horizon Shipping Co',
};

const EMPTY_FORM = {
  po: '', invoice: '', customerName: '', shippingAddress: '',
  itemSize: '', weightLimit: '', phone: '', expressDelivery: false, smsAlerts: false,
};

export default function ShippingApp() {
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorError, setVendorError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [orders, setOrders] = useState([]);
  const vendorLocated = !!vendorName;

  const handleLocate = () => {
    const trimmed = vendorId.trim();
    if (!trimmed) { setVendorError('Please enter a Vendor ID.'); setVendorName(''); return; }
    const found = VENDORS[trimmed];
    if (found) { setVendorName(found); setVendorError(''); }
    else { setVendorName(''); setVendorError(`Vendor ID "${trimmed}" not found.`); }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!form.po.trim()) errors.po = 'PO# is required.';
    if (!form.invoice.trim()) errors.invoice = 'Invoice # is required.';
    if (!form.customerName.trim()) errors.customerName = 'Customer Name is required.';
    if (!form.shippingAddress.trim()) errors.shippingAddress = 'Shipping Address is required.';
    if (!form.itemSize.trim()) errors.itemSize = 'Item Size is required.';
    if (!form.weightLimit.trim()) errors.weightLimit = 'Weight Limit is required.';
    if (!form.phone.trim()) errors.phone = 'Phone is required.';
    return errors;
  };

  const handleSubmit = () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setOrders(prev => [{ id: Date.now(), vendor: vendorName, ...form }, ...prev]);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  return (
    <div className="sh-wrapper">
      <div className="sh-titlebar">
        <span>📦</span>
        <span className="sh-title">SHIPPING MANAGEMENT</span>
        <Link to="/" className="sh-back-link">← Back to Hub</Link>
      </div>
      <div className="sh-body">
        <div className="sh-vendor-bar">
          <label className="sh-vendor-label">Shipping Vendor ID</label>
          <input
            className={`sh-vendor-input${vendorError ? ' sh-input-error' : ''}`}
            value={vendorId}
            onChange={e => { setVendorId(e.target.value); setVendorError(''); setVendorName(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLocate()}
            placeholder="Enter Vendor ID"
          />
          <button className="sh-locate-btn" onClick={handleLocate}>Locate</button>
          {vendorName && <span className="sh-vendor-found">{vendorName}</span>}
          {vendorError && <span className="sh-vendor-error">{vendorError}</span>}
        </div>

        <div className="sh-divider" />
        <div className="sh-section-title">ORDER FORM</div>

        <div className={`sh-order-form${!vendorLocated ? ' sh-form-disabled' : ''}`}>
          <div className="sh-form-left">
            {[['po','PO#','Purchase Order #'],['invoice','Invoice #','Invoice Number'],['customerName','Customer Name','Full Name']].map(([name,label,ph]) => (
              <div className="sh-form-row" key={name}>
                <label>{label}</label>
                <div className="sh-input-wrap">
                  <input name={name} value={form[name]} onChange={handleFormChange}
                    className={formErrors[name] ? 'sh-input-error' : ''} placeholder={ph} disabled={!vendorLocated} />
                  {formErrors[name] && <span className="sh-field-error">{formErrors[name]}</span>}
                </div>
              </div>
            ))}
            <div className="sh-form-row sh-align-top">
              <label>Shipping Address</label>
              <div className="sh-input-wrap">
                <textarea name="shippingAddress" value={form.shippingAddress} onChange={handleFormChange}
                  className={formErrors.shippingAddress ? 'sh-input-error' : ''} placeholder="Full shipping address..." rows={4} disabled={!vendorLocated} />
                {formErrors.shippingAddress && <span className="sh-field-error">{formErrors.shippingAddress}</span>}
              </div>
            </div>
          </div>

          <div className="sh-form-divider-v" />

          <div className="sh-form-right">
            <div className="sh-form-row">
              <label>Item Size</label>
              <div className="sh-input-wrap sh-inline-unit">
                <input name="itemSize" value={form.itemSize} onChange={handleFormChange}
                  className={formErrors.itemSize ? 'sh-input-error' : ''} placeholder="e.g. 30x20" disabled={!vendorLocated} />
                <span className="sh-unit">Height x Width</span>
              </div>
              {formErrors.itemSize && <span className="sh-field-error sh-block-err">{formErrors.itemSize}</span>}
            </div>
            <div className="sh-form-row">
              <label>Weight Limit</label>
              <div className="sh-input-wrap sh-inline-unit">
                <input name="weightLimit" value={form.weightLimit} onChange={handleFormChange}
                  className={formErrors.weightLimit ? 'sh-input-error' : ''} placeholder="e.g. 25" disabled={!vendorLocated} />
                <span className="sh-unit">Kg(s)</span>
              </div>
              {formErrors.weightLimit && <span className="sh-field-error sh-block-err">{formErrors.weightLimit}</span>}
            </div>
            <div className="sh-form-row">
              <label>Phone</label>
              <div className="sh-input-wrap">
                <input name="phone" value={form.phone} onChange={handleFormChange}
                  className={formErrors.phone ? 'sh-input-error' : ''} placeholder="Contact number" disabled={!vendorLocated} />
                {formErrors.phone && <span className="sh-field-error">{formErrors.phone}</span>}
              </div>
            </div>
            <div className="sh-form-row-opts">
              <div className="sh-checkboxes">
                <label className="sh-chk-label"><input type="checkbox" name="expressDelivery" checked={form.expressDelivery} onChange={handleFormChange} disabled={!vendorLocated} /> Express Delivery</label>
                <label className="sh-chk-label"><input type="checkbox" name="smsAlerts" checked={form.smsAlerts} onChange={handleFormChange} disabled={!vendorLocated} /> SMS Alerts</label>
              </div>
              <button className="sh-submit-btn" onClick={handleSubmit} disabled={!vendorLocated}>Submit</button>
            </div>
          </div>
        </div>

        <div className="sh-divider" />

        <div className="sh-table-wrap">
          <table className="sh-table">
            <thead>
              <tr>{['PO #','Invoice #','Customer Name','Item Size','Weight Limit','Phone','Express','SMS','Vendor'].map(h=><th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {orders.length === 0
                ? <tr><td colSpan="9" className="sh-no-orders">No orders submitted yet.</td></tr>
                : orders.map(o => (
                  <tr key={o.id}>
                    <td>{o.po}</td><td>{o.invoice}</td><td>{o.customerName}</td>
                    <td>{o.itemSize}</td><td>{o.weightLimit} Kg</td><td>{o.phone}</td>
                    <td><span className={`sh-badge ${o.expressDelivery?'sh-badge-yes':'sh-badge-no'}`}>{o.expressDelivery?'Yes':'No'}</span></td>
                    <td><span className={`sh-badge ${o.smsAlerts?'sh-badge-yes':'sh-badge-no'}`}>{o.smsAlerts?'Yes':'No'}</span></td>
                    <td>{o.vendor}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
